import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  Form,
  FormButton,
  Input,
  useNotifications
} from '@ijoin/components';
import {
  AnnuityCarrierExportRes,
  getGetAnnuityCarrierExportsQueryKey,
  usePatchAnnuityCarrierExport
} from '@ijoin/hooks';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ApiError } from '../types';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type FormValues = {
  fileIdentifier: string;
};

export const EditFileIdentifierDialog: FC<{
  annuityIntegrationId: string;
  exportRow: AnnuityCarrierExportRes;
  onClose: () => void;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const methods = useForm<FormValues>({
    defaultValues: { fileIdentifier: props.exportRow.fileIdentifier ?? '' },
    mode: 'all',
    resolver: zodResolver(
      z.object({
        fileIdentifier: z
          .string()
          .trim()
          .refine(value => !value || UUID_PATTERN.test(value), {
            message: 'File identifier must be a valid UUID'
          })
      })
    )
  });

  const updateExport = usePatchAnnuityCarrierExport<ApiError>({
    mutation: {
      onError: error =>
        notifications.error(error.message || 'Failed to update export'),
      onSuccess: () => {
        notifications.success('Export updated');
        queryClient.invalidateQueries({
          queryKey: getGetAnnuityCarrierExportsQueryKey(
            props.annuityIntegrationId
          )
        });
        props.onClose();
      }
    }
  });

  const onSubmit = (data: FormValues) => {
    updateExport.mutate({
      annuityIntegrationId: props.annuityIntegrationId,
      data: { fileIdentifier: data.fileIdentifier.trim() },
      id: props.exportRow.id
    });
  };

  return (
    <Dialog
      data-testid={`editFileIdentifier-${props.exportRow.id}`}
      fullWidth
      isOpen
      maxWidth='xs'
      onClose={props.onClose}>
      <Form
        methods={methods}
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>Edit File Identifier</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Input
              format='string'
              fullWidth
              label='File Identifier'
              name='fileIdentifier'
              size='small'
            />
            {!props.exportRow.sentToCarrierDate && (
              <Alert
                data-testid='clearFileIdentifierWarning'
                severity='warning'>
                Clearing the file identifier on a row that has not been sent to
                the carrier will include it in the next Retirement Edge batch.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid={`cancelEditFileIdentifier-${props.exportRow.id}`}
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            data-testid={`saveEditFileIdentifier-${props.exportRow.id}`}
            triggerValidation
            variant='contained'>
            Save
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

EditFileIdentifierDialog.displayName = 'EditFileIdentifierDialog';
